import classNames from 'classnames';
import React from 'react';
import type { Author } from '../../types';
import { serializerFlexAlignment } from '../../utils/serializerFlexAlignment';
import type { CardSchema, TAuthorAttributes } from '../Authors.type';
import Bio from './Bio';
import Image from './Image';
import Name from './Name';
import Socials from './Socials';

const cardSchema: CardSchema = {
  node: 'div',
  className: 'flex flex-col gap-4',
  children: [
    {
      node: 'image',
      className: 'object-cover h-16 w-16',
      children: [],
    },
    {
      node: 'div',
      className: 'flex flex-col',
      children: [
        {
          node: 'name',
          className: 'flex flex-col',
          children: [],
        },
        {
          node: 'bio',
          className: 'flex flex-col',
          children: [],
        },
      ],
    },
    {
      node: 'socials',
      className: 'flex flex-row gap-2',
      children: [],
    },
  ],
};

interface SerializerProps {
  author: Author;
  attributes: TAuthorAttributes;
}

const Serializer: React.FC<SerializerProps> = ({ author, attributes }) => {
  const renderNode = (node: CardSchema): React.ReactNode => {
    switch (node.node) {
      case 'div': {
        const flexAlignmentClass = serializerFlexAlignment({
          alignment: attributes?.alignment || ('left' as any),
          includesFlexRow: node.className.includes('flex-row'),
          includesFlexColumn: node.className.includes('flex-col'),
        });

        return (
          <div className={classNames(node.className, flexAlignmentClass)}>
            {node.children.map((child: CardSchema, index: number) => (
              <React.Fragment key={index}>{renderNode(child)}</React.Fragment>
            ))}
          </div>
        );
      }
      case 'image':
        return (
          <Image
            className={classNames(node.className)}
            imageUrl={author?.profile_picture?.url}
            alt={author?.name}
            attributes={attributes}
          />
        );
      case 'name':
        return <Name className={classNames(node.className)} name={author?.name} attributes={attributes} />;
      case 'bio':
        return <Bio className={classNames(node.className)} bio={author?.bio} attributes={attributes} />;
      case 'socials':
        return (
          <Socials
            className={classNames(node.className)}
            attributes={attributes}
            twitter_handle={author?.twitter_handle}
            facebook_url={author?.facebook_url}
            instagram_url={author?.instagram_url}
            linkedin_url={author?.linkedin_url}
            tiktok_url={author?.tiktok_url}
            youtube_url={author?.youtube_url}
            discord_url={author?.discord_url}
          />
        );
      default:
        return null;
    }
  };

  return <>{renderNode(attributes?.cardStructure || cardSchema)}</>;
};

export default Serializer;
